/**
 * HTML conversion utilities for Quill editor
 *
 * These functions handle the conversion between:
 * - Quill's internal HTML format (uses <p><br></p> for empty lines)
 * - Storage/Handlebars format (uses <br><br> for line breaks, no paragraph wrappers)
 */

/**
 * Convert stored HTML format to Quill-friendly format
 * - Converts <br><br> to empty paragraphs (Quill's blank line format)
 * - Converts <b> to <strong>
 * - Wraps content in paragraphs if needed
 */
export function prepareHtmlForQuill(html: string | null): string {
  if (!html) return '';

  let processed = html;

  // Convert <b> to <strong> (Quill uses strong)
  processed = processed.replace(/<b>/gi, '<strong>');
  processed = processed.replace(/<\/b>/gi, '</strong>');

  // Fix malformed HTML: remove orphaned closing tags at start
  processed = processed.replace(/^<\/p>\s*/i, '');

  // Handle malformed: </p><br><br><p> should become </p><p><br></p><p>
  processed = processed.replace(/<\/p>\s*<br\s*\/?>\s*<br\s*\/?>\s*<p>/gi, '</p><p><br></p><p>');

  // Convert double BR to empty paragraph (Quill's blank line)
  processed = processed.replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '</p><p><br></p><p>');

  // Wrap in paragraph if not already wrapped
  if (!processed.trim().startsWith('<p>') && !processed.trim().startsWith('<ul>') && !processed.trim().startsWith('<ol>')) {
    processed = '<p>' + processed;
  }
  if (!processed.trim().endsWith('</p>') && !processed.trim().endsWith('</ul>') && !processed.trim().endsWith('</ol>')) {
    processed = processed + '</p>';
  }

  // Clean up double wrapping
  processed = processed.replace(/<p>\s*<p>/gi, '<p>');
  processed = processed.replace(/<\/p>\s*<\/p>/gi, '</p>');

  return processed;
}

/**
 * Convert Quill HTML output to storage/Handlebars-compatible format
 * - Converts Quill's empty paragraphs (<p><br></p>) to <br><br>
 * - Removes paragraph wrappers while preserving structure
 * - Removes unwanted link attributes
 */
export function prepareHtmlForStorage(html: string | null | undefined): string {
  if (!html) return '';

  let processed = html;

  // Remove unwanted link attributes added by Quill
  processed = processed.replace(/\s*rel="noopener noreferrer"\s*/gi, '');
  processed = processed.replace(/\s*target="_blank"\s*/gi, '');

  // Fix mailto links (Quill sometimes changes them to about:blank)
  processed = processed.replace(
    /<a\s+href="about:blank"[^>]*>([^@<]+@[^<]+)<\/a>/gi,
    '<a href="mailto:$1">$1</a>'
  );

  // Step 1: Replace empty paragraphs with placeholder to preserve them
  const BLANK_LINE_PLACEHOLDER = '___BLANK_LINE___';
  processed = processed.replace(/<p><br><\/p>/gi, BLANK_LINE_PLACEHOLDER);

  // Step 2: Convert paragraph breaks to double BR
  processed = processed.replace(/<\/p>\s*<p>/gi, '<br><br>');

  // Step 3: Remove remaining paragraph tags
  processed = processed.replace(/<p>/gi, '');
  processed = processed.replace(/<\/p>/gi, '');

  // Step 4: Restore blank lines from placeholder
  processed = processed.replace(new RegExp(BLANK_LINE_PLACEHOLDER, 'g'), '<br><br>');

  // Clean up excessive consecutive line breaks (more than 2)
  processed = processed.replace(/(<br\s*\/?>){3,}/gi, '<br><br>');

  return processed;
}

/**
 * Like prepareHtmlForStorage, but returns null when the editor holds no text.
 * A cleared Quill editor still emits markup such as <p><br></p>, which would
 * otherwise be stored as <br><br> instead of clearing the column.
 */
export function prepareHtmlForStorageOrNull(html: string | null | undefined): string | null {
  const processed = prepareHtmlForStorage(html);
  return stripHtml(processed.replace(/&nbsp;/gi, ' ')) ? processed : null;
}

/**
 * Render HTML content as plain text by removing every tag and collapsing
 * whitespace. Use when a field that *may* contain HTML (e.g. legacy Quill
 * content) needs to be displayed as plain text in a non-rich-text context.
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}
